import type { ScreenDef } from '../../shared';
import { h, type SettingsPort } from '../../ui';
import { ATELIER_DRAFT, ATELIER_TRANSFER, ATELIER_BASELINE } from '../../shared/atelier';
import type { CreatureSave } from '../save';
import { saveAtelierHero } from './atelierSave';

/** The same GLB atelier is used standalone and inside the game, with one saved recipe. */
export const atelierScreen: ScreenDef = {
  id:'creature/make', route:'#/make[/:stage[/:part]]', aliases:['make'], titleKey:'creature.screen.make', needs3d:false, kind:'immersive', parseParams:p=>({ stage:p.get('stage')??'' }),
  create(ctx) {
    const goHome = (language: unknown) => {
      void ctx.router.goto('#/life', {}, { replace:true }).then(() => {
        const port = (ctx as typeof ctx & {settingsPort?:SettingsPort}).settingsPort;
        if ((language === 'en' || language === 'ru') && port?.get().lang !== language) port?.set({lang:language});
      });
    };
    if (ctx.params.stage === 'play') {
      let transfer: { lang?: unknown } | null = null;
      try {
        const raw = sessionStorage.getItem(ATELIER_TRANSFER);
        sessionStorage.removeItem(ATELIER_TRANSFER);
        if (raw) transfer = JSON.parse(raw);
      } catch { /* A missing or damaged handoff simply opens the normal atelier. */ }
      if (saveAtelierHero(ctx, transfer)) {
        const root = h('p', { attrs:{ role:'status' } }, transfer?.lang === 'en' ? 'Opening your chronicle…' : 'Открываем летопись…');
        return { root, enter(){ goHome(transfer?.lang); }, isIdle:()=>true, dispose(){} };
      }
    }
    const active = ctx.services.creatures.active();
    const saved = (ctx.store.state.slices.creature as CreatureSave).roster?.find(x => x.id === active?.id);
    try { sessionStorage.setItem(ATELIER_BASELINE, JSON.stringify(saved?.appearance ?? null)); } catch { /* Local comparison still works when session storage is unavailable. */ }
    if (saved?.appearance) { try { localStorage.setItem(ATELIER_DRAFT, JSON.stringify(saved.appearance)); } catch { /* iframe reports storage errors on save */ } }
    const lang = document.documentElement.lang === 'en' ? 'en' : 'ru';
    const settings = (ctx as typeof ctx & {settingsPort?:SettingsPort}).settingsPort?.get();
    const editorParams = new URLSearchParams({ embed:'1', restore:'1', lang,
      textScale:String(settings?.textScale ?? document.documentElement.dataset.textscale ?? 100),
      font:settings?.font ?? (document.documentElement.dataset.font === 'plain' ? 'plain' : 'calligraphic'),
      motion:settings?.motion ?? document.documentElement.dataset.motion ?? 'full',
    });
    const frame = h('iframe', { attrs:{ src:`/charlab.html?${editorParams}`, title:lang === 'en'?'The hero atelier':'Ателье героев', allow:'fullscreen' } });
    frame.style.cssText='position:fixed;inset:0;width:100%;height:100%;border:0;background:#183345;z-index:20';
    const root = h('section', { attrs:{ 'aria-label':lang === 'en'?'Create your hero':'Создание героя' } }, frame);
    let done = false;
    let readyDocument: Document | null = null;
    const receive = (event: MessageEvent): void => {
      if (event.origin !== location.origin || event.source !== frame.contentWindow || !event.data || done) return;
      if (event.data.type === 'marginalia:atelier:ready') { readyDocument=frame.contentDocument; return; }
      if (event.data.type === 'marginalia:atelier:exit') { void ctx.router.goto(active?'#/life':'#/title'); return; }
      if (event.data.type !== 'marginalia:atelier:save') return;
      if (!saveAtelierHero(ctx, event.data)) return;
      done = true;
      goHome(event.data.lang);
    };
    window.addEventListener('message', receive);
    return { root, render(){}, isIdle:()=>true, debugState:()=>({atelier:true}),
      onBack(){
        // A late image load belongs to the same ready editor; navigation creates
        // a different Document and must not inherit the old exit handler.
        if (!readyDocument || readyDocument !== frame.contentDocument) return false;
        frame.contentWindow?.postMessage({type:'marginalia:atelier:request-exit'}, location.origin);
        return true;
      },
      dispose(){ window.removeEventListener('message', receive); frame.remove(); } };
  },
};
